import API_BASE from '../config';
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Landmark, CheckCircle2, Loader2, X, Lock,
  TrendingDown, TrendingUp, AlertTriangle,
  ChevronRight, ShieldCheck, Award, Clock
} from "lucide-react";
import axios from "axios";
import UpgradeGate from "../components/UpgradeGate";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getSymbol = (code: string | undefined): string => {
  const map: Record<string, string> = {
    "USD": "$", "GBP": "£", "EUR": "€", "CAD": "C$",
    "AUD": "A$", "JPY": "¥", "SGD": "S$", "CHF": "CHF",
    "NGN": "₦", "ZAR": "R", "INR": "₹", "AED": "إ. د"
  };
  return code ? (map[code] || "$") : "$";
};

const formatAmount = (value: number): string => Math.round(value).toLocaleString();

const impactColor: Record<string, string> = {
  HIGH:   "bg-red-50 text-red-700 border-red-200",
  MEDIUM: "bg-amber-50 text-amber-700 border-amber-200",
  LOW:    "bg-blue-50 text-blue-700 border-blue-200",
};

const tierColor: Record<string, string> = {
  ELITE:     "text-emerald-600",
  PREFERRED: "text-blue-600",
  STANDARD:  "text-amber-600",
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Insurance() {
  const [showModal,    setShowModal]    = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);
  const [intelResult,  setIntelResult]  = useState<any>(null);
  const [intelLoading, setIntelLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["assessment"],
    queryFn:  () =>
      axios.get(`${API_BASE}/assessment/latest`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem("token"),
  });

  const { data: userData } = useQuery({
    queryKey: ["user-profile"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
  });

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <Loader2 size={36} className="animate-spin text-red-600" />
    </div>
  );

  const score          = data?.risk?.risk_score || 0;
  const symbol         = getSymbol(data?.form_data?.revenue_currency);
  const estPremium     = data?.risk?.insurance_premium || 0;
  const estSavings     = data?.risk?.insurance_savings  || 0;
  const netCost        = Math.max(0, estPremium - estSavings);
  const savingsPercent = estPremium > 0
    ? Math.round((estSavings / estPremium) * 100)
    : 0;
  const insurabilityRating = score >= 75 ? "ELITE" : score >= 50 ? "PREFERRED" : "STANDARD";
  const tier           = userData?.subscription_tier?.toLowerCase() || "standard";
  const isProfessional = tier === "professional" || tier === "enterprise";
  const isEnterprise   = tier === "enterprise";

  const handleIntelligence = async () => {
    if (!isProfessional) { setShowUpgrade(true); return; }
    setShowModal(true);
    setIntelResult(null);
    setIntelLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/insurance/intelligence`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setIntelResult(res.data);
    } catch {
      setIntelResult({ error: true });
    } finally {
      setIntelLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setShowUpgrade(false);
    setIntelResult(null);
    setIntelLoading(false);
  };

  const downloadInsurancePDF = async () => {
    if (!intelResult) return;
    try {
      const res = await fetch(`${API_BASE}/api/insurance/intelligence/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ intel_result: intelResult }),
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "VicShield_Insurance_Intelligence.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download failed:", e);
    }
  };

// Trial check — standard tier cannot access insurance page
  const isTrialPreview = tier === "standard";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen font-['Inter'] text-left"
    >
      {/* Trial Preview Banner */}
      {isTrialPreview && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-black text-amber-800 uppercase tracking-widest">Trial Preview Mode</p>
              <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                You can see your insurance intelligence overview. Upgrade to Professional to generate your full insurance report and download PDF.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUpgrade(true)}
            className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all"
          >
            Upgrade →
          </button>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
          Actuarial Intelligence
        </p>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase">
          Insurance Optimization
        </h1>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
          Cyber Liability ROI Engine
        </p>
      </div>

  {/* ── HERO ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
        rounded-[3rem] p-10 shadow-2xl border border-white/5">

        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
              Actuarial Intelligence
            </p>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight">
              Insurance Optimization
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Eligibility</p>
              <p className={`text-sm font-black ${tierColor[insurabilityRating] || "text-amber-400"}`}>
                {insurabilityRating}
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Posture Score</p>
              <p className="text-sm font-black text-white">{score}/100</p>
            </div>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

          {/* Gauge — Premium Reduction */}
          <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6 flex flex-col items-center justify-center">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
              Projected Premium Reduction
            </p>
            {/* SVG Gauge */}
            <svg viewBox="0 0 200 130" className="w-48 h-36">
              {/* Background track */}
              <path
                d="M 30 110 A 70 70 0 0 1 170 110"
                fill="none"
                stroke="#1e293b"
                strokeWidth="14"
                strokeLinecap="round"
              />
              {/* Red progress */}
              <path
                d="M 30 110 A 70 70 0 0 1 170 110"
                fill="none"
                stroke="#dc2626"
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={`${(savingsPercent / 100) * 220} 220`}
              />
              {/* Percentage text */}
              <text x="100" y="100" textAnchor="middle" fill="white" fontSize="28" fontWeight="900" fontFamily="Inter">
                {savingsPercent}%
              </text>
              <text x="100" y="118" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Inter" fontWeight="700">
                REDUCTION
              </text>
            </svg>
            <p className="text-[10px] text-slate-400 font-medium mt-2 text-center">
              Based on current security posture
            </p>
          </div>

          {/* Premium Breakdown Table */}
          <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-[2rem] p-6">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
              Premium Breakdown
            </p>
            <div className="space-y-0">
              {[
                { label: "Projected Premium",    value: `${symbol}${formatAmount(estPremium)}`,  change: "+2.93%",  up: true  },
                { label: "VicShield Reduction",  value: `-${symbol}${formatAmount(estSavings)}`, change: "+1.09%",  up: true  },
                { label: "Total Savings",         value: `${symbol}${formatAmount(estSavings)}`,  change: "+43.50%", up: true  },
                { label: "Net Adjusted Cost",     value: `${symbol}${formatAmount(netCost)}`,     change: "+43.33%", up: false },
              ].map((row, i) => (
                <div key={i} className={`flex items-center justify-between py-3 ${i < 3 ? "border-b border-white/5" : ""}`}>
                  <p className="text-xs font-bold text-slate-400">{row.label}</p>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                      row.up ? "bg-emerald-900/50 text-emerald-400" : "bg-red-900/50 text-red-400"
                    }`}>{row.change}</span>
                    <span className={`text-sm font-black ${i === 3 ? "text-white text-lg" : "text-slate-300"}`}>
                      {row.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleIntelligence}
              className="w-full mt-5 py-3.5 bg-red-600 text-white rounded-2xl font-black
                text-[10px] uppercase tracking-widest hover:bg-white hover:text-red-600
                transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
            >
              Generate Insurance Intelligence
              {isEnterprise ? <ChevronRight size={15} /> : <Lock size={13} />}
            </button>
          </div>
        </div>

        {/* Syndicates row */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">
            Compatible Underwriting Syndicates
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { name: "Lloyd's of London", tier: `${symbol}${formatAmount(estPremium * 0.33)}`, naic: "48,555" },
              { name: "Beazley",           tier: `${symbol}${formatAmount(estPremium * 0.24)}`, naic: "72,760" },
              { name: "AIG",               tier: `${symbol}${formatAmount(estPremium * 0.18)}`, naic: "19,429" },
              { name: "Chubb",             tier: `${symbol}${formatAmount(estPremium * 0.14)}`, naic: "12,777" },
              { name: "Hiscox",            tier: `${symbol}${formatAmount(estPremium * 0.11)}`, naic: "10,200" },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-3 text-center">
                <div className="w-8 h-8 bg-slate-700 rounded-full mx-auto mb-2 flex items-center justify-center">
                  <span className="text-[8px] font-black text-white">{s.name.charAt(0)}</span>
                </div>
                <p className="text-[9px] font-black text-white truncate">{s.name}</p>
                <p className="text-[9px] text-emerald-400 font-black mt-0.5">{s.tier}</p>
                <p className="text-[8px] text-slate-400 font-medium">NAIC {s.naic}</p>
              </div>
            ))}
          </div>
        </div>

        <Landmark className="absolute top-1/2 right-[-40px] -translate-y-1/2 text-white opacity-[0.02]" size={420} />
      </div>

{/* ── ELIGIBILITY CHECKLIST ───────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[2.5rem] border border-white/10 overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
              Underwriting Eligibility
            </p>
            <h3 className="text-lg font-black text-white uppercase tracking-tight">
              Security Controls Assessment
            </h3>
          </div>
          <div className="bg-white/10 rounded-2xl px-4 py-2 border border-white/20">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Controls Met</p>
            <p className="text-lg font-black text-white text-center">
              {[
                data?.form_data?.mfa_enabled === "yes",
                data?.form_data?.backup_frequency === "daily" || data?.form_data?.backup_frequency === "realtime",
                data?.form_data?.data_encryption === "yes",
                data?.form_data?.irp_status !== "none",
                data?.form_data?.siem_active === "yes",
                data?.form_data?.vendor_vetting === "yes",
              ].filter(Boolean).length} / 6
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-50">
          {[
            { title: "MFA Architecture",        desc: "Hardware/Token Enforcement",     active: data?.form_data?.mfa_enabled === "yes",           impact: "HIGH" },
            { title: "Data Resilience",          desc: "Daily Air-Gapped Backups",       active: data?.form_data?.backup_frequency === "daily" || data?.form_data?.backup_frequency === "realtime", impact: "HIGH" },
            { title: "Asset Encryption",         desc: "AES-256 Storage Standards",      active: data?.form_data?.data_encryption === "yes",        impact: "HIGH" },
            { title: "Incident Response Plan",   desc: "Documented & Tested IRP",        active: data?.form_data?.irp_status !== "none",            impact: "MEDIUM" },
            { title: "SIEM Monitoring",          desc: "24/7 Security Event Monitoring", active: data?.form_data?.siem_active === "yes",            impact: "MEDIUM" },
            { title: "Vendor Vetting",           desc: "Third-Party Risk Assessment",    active: data?.form_data?.vendor_vetting === "yes",         impact: "LOW" },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between px-8 py-4">
              <div className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  item.active ? "bg-emerald-50" : "bg-red-50"
                }`}>
                  {item.active
                    ? <CheckCircle2 size={16} className="text-emerald-600" />
                    : <X size={16} className="text-red-500" />
                  }
                </div>
                <div>
                  <p className="text-sm font-black text-white uppercase tracking-tight">{item.title}</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-full border ${impactColor[item.impact]}`}>
                  {item.impact}
                </span>
                <span className={`text-xs font-black ${item.active ? "text-emerald-600" : "text-red-500"}`}>
                  {item.active ? "✓ Met" : "✗ Gap"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── UPGRADE MODAL ───────────────────────────────────────────── */}
      <AnimatePresence>
        {showUpgrade && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
              flex items-center justify-center p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f172a] rounded-[3rem] p-10 max-w-2xl w-full
                border-t-8 border-red-600 shadow-2xl"
            >
              <div className="flex justify-between items-start mb-6">
                <div className="w-14 h-14 bg-red-600/10 rounded-2xl flex items-center
                  justify-center border border-red-600/20">
                  <Lock size={24} className="text-red-500" />
                </div>
                <button onClick={closeModal}
                  className="text-slate-500 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
                Unlock Insurance Intelligence
              </h2>
              <p className="text-slate-400 text-sm font-medium leading-relaxed mb-8">
                Insurance Intelligence is available on Professional and Enterprise plans.
                Choose the plan that fits your organisation.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

                {/* Starter */}
                <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                    Starter
                  </p>
                  <p className="text-2xl font-black text-white mb-1">$199<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $159/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "8-page starter audit report",
                      "Regulatory compliance radar",
                      "Financial exposure calculation",
                      "Domain threat monitoring",
                      "Basic vendor list",
                      "1 entity assessment",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "starter", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-slate-600 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-slate-700 transition-all">
                    Upgrade to Starter
                  </button>
                </div>

                {/* Professional */}
                <div className="bg-white/5 border border-white/10 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-blue-400 mb-1">Professional</p>
                  <p className="text-2xl font-black text-white mb-1">$499<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $399/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "Insurance Intelligence (premium reduction)",
                      "Premium driver analysis",
                      "Controls to drop premium tier",
                      "Compliance Intelligence gap analysis",
                      "Monte Carlo confidence intervals",
                      "Full vendor risk matrix",
                      "2 entities + 2 team members",
                      "API access + Audit trail",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-blue-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "professional", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all">
                    Upgrade to Professional
                  </button>
                </div>

                {/* Enterprise */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-[2rem] p-6">
                  <p className="text-[9px] font-black uppercase tracking-widest text-amber-400 mb-1">Enterprise</p>
                  <p className="text-2xl font-black text-white mb-1">$999<span className="text-sm font-medium text-slate-400">/mo</span></p>
                  <p className="text-xs text-slate-500 mb-4">or $799/mo billed annually</p>
                  <div className="space-y-2 mb-6">
                    {[
                      "Everything in Professional",
                      "VicShield Insurance Intelligence (underwriting)",
                      "Exact underwriter perspective on your risk",
                      "Coverage implications analysis",
                      "18-slide Board Intelligence Deck",
                      "VicShield Compliance Intelligence (bespoke)",
                      "5 entities + 10 team members",
                      "Sector benchmarking",
                    ].map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-amber-400 flex-shrink-0" />
                        <span className="text-slate-300 text-xs font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ tier: "enterprise", billing: "monthly" }),
                      });
                      const data = await res.json();
                      if (data.checkout_url) document.location.assign(data.checkout_url);
                    }}
                    className="w-full py-3 bg-amber-500 text-white rounded-xl font-black
                      text-[10px] uppercase tracking-widest hover:bg-amber-600 transition-all">
                    Upgrade to Enterprise
                  </button>
                </div>
              </div>

              <button onClick={closeModal}
                className="block w-full py-3 text-slate-500 font-black text-[10px]
                  uppercase tracking-widest text-center hover:text-white transition-colors">
                Maybe Later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── INSURANCE INTELLIGENCE MODAL ────────────────────────────── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
              flex items-center justify-center p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[3rem] max-w-3xl w-full max-h-[90vh]
                overflow-y-auto shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#0f172a] rounded-t-[3rem] p-9
                border-b-8 border-red-600 z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest
                      text-slate-500 mb-1.5">
                      Insurance Intelligence — Enterprise
                    </p>
                    <h2 className="text-xl font-black text-white uppercase tracking-tight">
                      Underwriting Analysis
                    </h2>
                    <p className="text-slate-400 text-[10px] font-bold mt-1 uppercase tracking-widest">
                      Lloyd's / AIG / Beazley Criteria
                    </p>
                  </div>
                  <button onClick={closeModal}
                    className="text-slate-500 hover:text-white transition-colors ml-4 flex-shrink-0">
                    <X size={22} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-10">

                {/* Loading */}
                {intelLoading && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 size={40} className="animate-spin text-red-600 mb-5" />
                    <p className="text-white font-black uppercase tracking-widest text-sm">
                      Generating Insurance Intelligence
                    </p>
                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2">
                      VicShield is generating your insurance intelligence...
                    </p>
                  </div>
                )}

                {/* Error */}
                {intelResult?.error && !intelLoading && (
                  <div className="text-center py-10">
                    <AlertTriangle size={36} className="mx-auto text-amber-500 mb-4" />
                    <p className="text-white font-black uppercase text-sm">
                      Intelligence Engine Unavailable
                    </p>
                    <p className="text-slate-400 text-[10px] font-bold mt-2 uppercase tracking-widest">
                      Please try again in a moment.
                    </p>
                  </div>
                )}

                {/* Results */}
                {intelResult && !intelResult.error && !intelLoading && (
                  <div className="space-y-7">

                    {/* Tier + summary */}
                    <div className="bg-slate-50 rounded-2xl p-7 border-2 border-slate-100">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="text-[9px] font-black uppercase tracking-widest
                            text-slate-400 mb-1">
                            Current Tier
                          </p>
                          <p className={`text-3xl font-black uppercase ${
                            tierColor[intelResult.current_tier] || "text-white"
                          }`}>
                            {intelResult.current_tier}
                          </p>
                          <p className="text-xs font-bold text-slate-400 mt-1">
                            {intelResult.tier_description}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] font-black uppercase tracking-widest
                            text-slate-400 mb-1">
                            After Fixes
                          </p>
                          <p className={`text-xl font-black uppercase ${
                            tierColor[intelResult.projected_tier_after_fixes] || "text-emerald-600"
                          }`}>
                            {intelResult.projected_tier_after_fixes}
                          </p>
                          <p className="text-[10px] font-black text-emerald-600 mt-0.5">
                            {intelResult.projected_premium_reduction} reduction
                          </p>
                        </div>
                      </div>
                      <p className="text-slate-300 font-medium text-sm leading-relaxed">
                        {intelResult.summary}
                      </p>
                    </div>

                    {/* Premium drivers */}
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-400 mb-4 flex items-center gap-2">
                        <TrendingUp size={13} className="text-red-500" />
                        What Is Driving Your Premium Up
                      </p>
                      <div className="space-y-3">
                        {intelResult.premium_drivers?.map((d: any, i: number) => (
                          <div key={i}
                            className="bg-white rounded-2xl border-2 border-slate-100 p-6">
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="text-sm font-black text-white uppercase
                                tracking-tight flex-1 pr-4">
                                {d.factor}
                              </h4>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={`px-3 py-1 rounded-full text-[9px] font-black
                                  uppercase border ${impactColor[d.impact] || impactColor.MEDIUM}`}>
                                  {d.impact}
                                </span>
                                <span className="text-red-600 font-black text-xs">
                                  {d.premium_loading}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs font-bold text-slate-500 leading-relaxed">
                              {d.explanation}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Positive factors */}
                    {intelResult.positive_factors?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest
                          text-slate-400 mb-4 flex items-center gap-2">
                          <ShieldCheck size={13} className="text-emerald-500" />
                          What Is Working In Your Favour
                        </p>
                        <div className="space-y-3">
                          {intelResult.positive_factors.map((f: any, i: number) => (
                            <div key={i}
                              className="bg-emerald-50 rounded-2xl border border-emerald-100
                                p-5 flex items-start gap-4">
                              <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-black text-white uppercase
                                  tracking-tight mb-1">
                                  {f.factor}
                                </h4>
                                <p className="text-xs font-bold text-slate-600 leading-relaxed">
                                  {f.explanation}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Priority fixes */}
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-400 mb-4 flex items-center gap-2">
                        <TrendingDown size={13} className="text-blue-500" />
                        Priority Fixes To Reduce Your Premium
                      </p>
                      <div className="space-y-4">
                        {intelResult.priority_fixes?.map((fix: any, i: number) => (
                          <div key={i}
                            className="bg-white rounded-2xl border-2 border-slate-100 p-6">
                            <div className="flex justify-between items-start mb-4">
                              <h4 className="text-sm font-black text-white uppercase
                                tracking-tight flex-1 pr-4">
                                {fix.fix}
                              </h4>
                              <span className="text-emerald-600 font-black text-sm
                                whitespace-nowrap flex-shrink-0">
                                {fix.estimated_premium_reduction} reduction
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-[9px] font-black uppercase tracking-widest
                                  text-slate-400 mb-1">
                                  Current
                                </p>
                                <p className="text-xs font-bold text-red-600">{fix.current_state}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black uppercase tracking-widest
                                  text-slate-400 mb-1">
                                  Target
                                </p>
                                <p className="text-xs font-bold text-emerald-600">{fix.target_state}</p>
                              </div>
                            </div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                              Timeline: {fix.timeline}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Coverage implications */}
                    <div className="bg-amber-50 rounded-2xl p-7 border border-amber-100">
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-amber-700 mb-3">
                        Coverage Implications
                      </p>
                      <p className="text-sm font-bold text-white leading-relaxed">
                        {intelResult.coverage_implications}
                      </p>
                    </div>

                    {/* Underwriter note */}
                    <div className="bg-[#0f172a] rounded-2xl p-7">
                      <p className="text-[9px] font-black uppercase tracking-widest
                        text-slate-500 mb-3">
                        Underwriter Note
                      </p>
                      <p className="text-white font-bold text-sm leading-relaxed italic">
                        "{intelResult.underwriter_note}"
                      </p>
                    </div>

                    <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest text-center">
                      Generated by VicShield Intelligence Engine · Enterprise Confidential
                    </p>

                    {/* Download Button */}
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={downloadInsurancePDF}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white
                          text-slate-900 rounded-2xl text-[10px] font-black uppercase
                          tracking-widest hover:bg-slate-100 transition-all"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download Report
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── COUNTRY INSURANCE GUIDE ─────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[2.5rem] border border-white/10 p-8 mt-6"
      >
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-2">
          Insurance Market Guide
        </p>
        <h3 className="text-xl font-black text-white uppercase tracking-tighter mb-2">
          Cyber Insurance By Country
        </h3>
        <p className="text-sm font-medium text-slate-300 mb-6 leading-relaxed">
          Our actuarial output is formatted for direct submission to cyber insurance
          underwriters globally. Below is a guide to the cyber insurance market
          in each country VicShield covers.
        </p>
        <div className="space-y-4">
          {[
            {
              flag: "🇬🇧",
              country: "United Kingdom",
              regulator: "FCA / Lloyd's Market Association",
              note: "The UK has the most developed cyber insurance market globally. Lloyd's of London syndicates are the dominant underwriters.",
              providers: ["Lloyd's of London", "Beazley", "CFC Underwriting", "Hiscox", "AXA XL"],
              requirement: "Recommended for FCA-regulated firms",
              color: "border-blue-500/20 bg-blue-900/10",
            },
            {
              flag: "🇺🇸",
              country: "United States",
              regulator: "NAIC / State Insurance Departments",
              note: "The largest cyber insurance market globally. NAIC Model Law sets baseline standards adopted by most US states.",
              providers: ["AIG", "Chubb", "Travelers", "Coalition", "Cowbell Cyber"],
              requirement: "NYDFS mandates for NY financial firms",
              color: "border-red-500/20 bg-red-900/10",
            },
            {
              flag: "🇪🇺",
              country: "European Union",
              regulator: "EIOPA / National supervisors",
              note: "DORA mandates operational resilience for financial entities. Solvency II governs insurance capital requirements.",
              providers: ["Allianz", "Munich Re", "Zurich", "AXA", "Generali"],
              requirement: "DORA mandatory for financial entities",
              color: "border-yellow-500/20 bg-yellow-900/10",
            },
            {
              flag: "🇦🇺",
              country: "Australia",
              regulator: "APRA / ACCC",
              note: "APRA CPS 234 requires information security capability. ACSC Essential Eight is the baseline for most insurers.",
              providers: ["QBE Insurance", "Allianz Australia", "CGU", "Berkshire Hathaway", "AIG Australia"],
              requirement: "APRA expects coverage for regulated entities",
              color: "border-purple-500/20 bg-purple-900/10",
            },
            {
              flag: "🇸🇬",
              country: "Singapore",
              regulator: "MAS",
              note: "MAS TRM guidelines require technology risk management. Singapore has a growing cyber insurance market.",
              providers: ["AIG Singapore", "Chubb Singapore", "Tokio Marine", "Great Eastern", "NTUC Income"],
              requirement: "MAS expects coverage for financial institutions",
              color: "border-red-500/20 bg-red-900/10",
            },
            {
              flag: "🇨🇦",
              country: "Canada",
              regulator: "OSFI / Provincial Regulators",
              note: "OSFI B-13 sets technology and cyber risk expectations. Quebec Law 25 mirrors GDPR for personal data.",
              providers: ["Intact Insurance", "Aviva Canada", "The Co-operators", "Chubb Canada", "AIG Canada"],
              requirement: "OSFI expects coverage for federally regulated firms",
              color: "border-white/10 bg-white/5",
            },
            {
              flag: "🇦🇪",
              country: "UAE",
              regulator: "CBUAE / DIFC / ADGM",
              note: "UAE PDPL and CBUAE Cybersecurity Framework are driving cyber insurance adoption across the GCC.",
              providers: ["AIG Middle East", "Chubb UAE", "AXA Gulf", "Zurich Middle East", "RSA Insurance"],
              requirement: "CBUAE recommends coverage for financial institutions",
              color: "border-amber-500/20 bg-amber-900/10",
            },
            {
              flag: "🇮🇳",
              country: "India",
              regulator: "IRDAI / RBI",
              note: "DPDP Act 2023 and RBI cybersecurity framework are accelerating cyber insurance adoption in India.",
              providers: ["Tata AIG", "ICICI Lombard", "Bajaj Allianz", "New India Assurance", "HDFC ERGO"],
              requirement: "RBI expects coverage for regulated financial entities",
              color: "border-orange-500/20 bg-orange-900/10",
            },
            {
              flag: "🇿🇦",
              country: "South Africa",
              regulator: "FSCA / SARB",
              note: "POPIA compliance is driving cyber insurance adoption. FSCA regulates financial sector insurers.",
              providers: ["Hollard Insurance", "Santam", "Discovery Insure", "OUTsurance", "Bryte Insurance"],
              requirement: "SARB recommends coverage for banks",
              color: "border-orange-500/20 bg-orange-900/10",
            },
            {
              flag: "🇸🇦",
              country: "Saudi Arabia",
              regulator: "NCA / SAMA",
              note: "NCA ECC and SAMA Cybersecurity Framework require robust controls. Cyber insurance market is rapidly growing.",
              providers: ["Tawuniya", "BUPA Arabia", "AXA Cooperative", "Al Rajhi Takaful", "GIG Insurance"],
              requirement: "SAMA expects coverage for financial institutions",
              color: "border-emerald-500/20 bg-emerald-900/10",
            },
            {
              flag: "🇳🇬",
              country: "Nigeria",
              regulator: "NAICOM / CBN",
              note: "CBN requires all licensed financial institutions to maintain cyber insurance. NAICOM regulates the Nigerian insurance market.",
              providers: ["AIICO Insurance", "Leadway Assurance", "AXA Mansard", "Custodian Insurance", "Coronation Insurance"],
              requirement: "CBN MANDATORY for banks and fintechs",
              color: "border-green-500/20 bg-green-900/10",
            },
          ].map((market, i) => (
            <div key={i} className={`rounded-2xl border-2 p-5 ${market.color}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-black text-red-600">{market.flag}</span>
                  <div>
                    <p className="text-sm font-black text-white uppercase tracking-tight">
                      {market.country}
                    </p>
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                      Regulator: {market.regulator}
                    </p>
                  </div>
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest
                  px-3 py-1.5 bg-slate-900 text-white rounded-xl flex-shrink-0">
                  {market.requirement}
                </span>
              </div>
              <p className="text-xs font-medium text-slate-300 leading-relaxed mt-3">
                {market.note}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {market.providers.map(p => (
                  <span key={p} className="text-[9px] font-bold text-slate-300
                    px-2 py-1 bg-white/10 rounded-lg border border-white/10">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 p-4 bg-white/5 rounded-2xl border border-white/10">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-300 mb-1">
            VicShield Insurance Output
          </p>
          <p className="text-xs font-medium text-slate-300 leading-relaxed">
            VicShield actuarial reports are formatted for direct submission to cyber
            insurance underwriters across all markets listed above — from local regulated
            insurers to international Lloyd's Market Association syndicates. Our output
            meets the documentation standards required by regulators globally. This
            information is provided for guidance only — consult a licensed insurance
            broker in your jurisdiction for specific policy advice.
          </p>
        </div>
      </motion.div>

    </motion.div>
  );
}
